import { Avatar, HStack, Text, theme, VStack } from "@chakra-ui/react";
import {
  AsyncCreatableSelect,
  AsyncSelect,
  chakraComponents,
  CreatableSelect,
  MenuListProps,
  OptionProps,
  Select as CSelect,
} from "chakra-react-select";
import { inspecto, toColor } from "modules/utils";
import React from "react";
const MenuList = chakraComponents.MenuList;
export const multiSelectStyles = (variant) => ({
  menu: (o) => ({
    ...o,
    overflow: "visible",
    px: "2",
    zIndex: 9999,
    minWidth: "100%",
    w: "auto",
  }),
  option: (o, b) => ({
    ...o,
    borderRadius: "8px",
    color: "gray.900",
    fontWeight: 500,
    bg: b.isFocused ? "gray.50" : b.isSelected ? "gray.100" : "white",
    _hover: {
      bg: "gray.50",
    },
    zIndex: 30,
  }),
  container: (o) => ({
    ...o,
    width: "100%",
    borderColor: variant === "ghost" ? "none" : "gray.200",
  }),
  dropdownIndicator: () => ({
    bg: "none",
    border: "none",
    color: "gray.500",
    mx: 2,
    zIndex: 999,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menuList: (o) => ({
    ...o,
    bg: "white",
    borderRadius: "8px",
    px: 2,
    zIndex: 9999,
  }),
  groupHeading: (o) => ({
    ...o,
    px: 2,
    color: "gray.500",
    fontWeight: "500",
    fontSize: "xs",
  }),
  multiValue: (a, b) => {
    const color = toColor(b.data.label as string);
    return {
      ...a,
      bg: color + ".100",
      color: color + ".700",
    };
  },
});

const NMenuList = ({ children, ...props }: MenuListProps) => {
  return (
    <MenuList {...props}>
      {
        Array.isArray(children)
          ? children.slice(0, 20) /* Options */
          : children /* NoOptionsLabel */
      }
    </MenuList>
  );
};

const NOption = ({ ...props }: OptionProps<any>) => {
  const opt = props.data;
  return (
    <chakraComponents.Option {...props}>
      <HStack w="full">
        {"image" in opt ? (
          typeof opt.image === "string" ? (
            <Avatar size="2xs" src={opt.image} name={opt.label} />
          ) : (
            opt.image
          )
        ) : null}
        <VStack align="flex-start" spacing={0}>
          <Text fontSize="sm">{opt.label}</Text>
          {opt.subtitle && (
            <Text fontSize="xs" color="gray.600">
              {opt.subtitle}
            </Text>
          )}
        </VStack>
      </HStack>
    </chakraComponents.Option>
  );
};
const MultiSelect = React.forwardRef((props: any, ref) => {
  const options =
    props?.options && Array.isArray(props.options) && props.options.length > 20
      ? [
          { label: "Search to filter options", value: null, disabled: true },
          ...props.options,
        ]
      : props.options;
  return props.createable ? (
    props.async ? (
      <AsyncCreatableSelect
        ref={ref}
        size={"sm"}
        colorScheme="p"
        focusBorderColor="p.300"
        styles={{
          menuPortal: (o) => ({
            ...o,
            zIndex: 9999,
          }),
        }}
        components={{ MenuList: NMenuList }}
        filterOption={optionMatches}
        chakraStyles={{
          ...multiSelectStyles(props.variant),
        }}
        {...props}
      />
    ) : (
      <CreatableSelect
        ref={ref}
        size={"sm"}
        colorScheme="p"
        components={{ MenuList: NMenuList, Option: NOption }}
        styles={{
          menuPortal: (o) => ({
            ...o,
            zIndex: 9999,
          }),
        }}
        chakraStyles={multiSelectStyles(props.variant)}
        {...props}
        focusBorderColor="p.300"
        filterOption={optionMatches}
        menuPosition={props.ignorePlacement ? undefined : "fixed"}
        menuPlacement="auto"
        menuPortalTarget={document.body}
        errorBorderColor={theme.colors["red"][500]}
      />
    )
  ) : props.async ? (
    <AsyncSelect
      size={"sm"}
      ref={ref}
      colorScheme="p"
      styles={{
        menuPortal: (o) => ({
          ...o,
          zIndex: 9999,
        }),
      }}
      filterOption={optionMatches}
      chakraStyles={multiSelectStyles(props.variant)}
      {...props}
      focusBorderColor="p.300"
      components={{ MenuList: NMenuList, Option: NOption }}
    />
  ) : (
    <CSelect
      ref={ref}
      size={"sm"}
      colorScheme="p"
      on
      chakraStyles={multiSelectStyles(props.variant)}
      {...props}
      styles={{
        menuPortal: (o) => ({
          ...o,
          zIndex: 9999,
        }),
      }}
      filterOption={optionMatches}
      noOptionsMessage={
        props.filterOpts ? () => "Search" : () => "No options found"
      }
      options={options}
      isOptionDisabled={(opt: any) => opt.disabled}
      components={{ MenuList: NMenuList, Option: NOption }}
      focusBorderColor="p.300"
      menuPosition={props.ignorePlacement ? undefined : "fixed"}
      menuPlacement="auto"
      menuPortalTarget={document.body}
      errorBorderColor={theme.colors["red"][500]}
    />
  );
});

const optionMatches = (option: any, searchVal?: string): boolean => {
  if (!searchVal) return true;
  const ds = searchVal.toLowerCase();
  if (option && option.label?.length && option.value?.length) {
    return (
      option.label.toLowerCase().includes(ds) ||
      option.value.toLowerCase().includes(ds) ||
      option.data?.subtitle?.toLowerCase()?.includes(ds)
    );
  } else {
    return false;
  }
};

export default MultiSelect;
